import { lazy, Suspense, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { Spinner } from '@/components/ui'
import {
  BuildingIcon,
  CameraIcon,
  ClipboardIcon,
  ContractIcon,
  FolderIcon,
  HomeIcon,
  LayersIcon,
  LinkIcon,
  LogoutIcon,
  MenuIcon,
  ShieldIcon,
  SparklesIcon,
  TargetIcon,
  UsersIcon,
  XIcon,
} from '@/components/ui/icons'
import { ROLE_LABEL, canMutate, isAdmin, logout, useCurrentUser } from '@/lib/api/auth'
import { useProjects } from '@/lib/api/projects'
import { useProjectSelection } from '@/lib/projectWorkspace'

const BimCompare = lazy(() => import('@/pages/BimCompare'))
const Contracts = lazy(() => import('@/pages/Contracts'))
const Defects = lazy(() => import('@/pages/Defects'))
const DesignPredict = lazy(() => import('@/pages/DesignPredict'))
const Home = lazy(() => import('@/pages/Home'))
const Inspection = lazy(() => import('@/pages/Inspection'))
const Integrity = lazy(() => import('@/pages/Integrity'))
const Login = lazy(() => import('@/pages/Login'))
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail'))
const Projects = lazy(() => import('@/pages/Projects'))
const PublicVerify = lazy(() => import('@/pages/PublicVerify'))
const SitePhoto = lazy(() => import('@/pages/SitePhoto'))
const Users = lazy(() => import('@/pages/Users'))

type NavItem = {
  path: string
  label: string
  code?: string
  icon: ReactNode
  adminOnly?: boolean
}

type NavGroup = {
  label: string
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: '워크스페이스',
    items: [
      { path: '/', label: '작업 현황', icon: <HomeIcon size={18} /> },
      { path: '/projects', label: '프로젝트', icon: <FolderIcon size={18} /> },
    ],
  },
  {
    label: '검측 흐름',
    items: [
      { path: '/bim-compare', label: 'BIM 비교', code: '1', icon: <LayersIcon size={18} /> },
      { path: '/site-photo', label: '현장 사진 매칭', code: '2', icon: <CameraIcon size={18} /> },
      { path: '/inspection', label: '검측·보고서', code: '3', icon: <ClipboardIcon size={18} /> },
      { path: '/defects', label: '하자·시정조치', code: '4', icon: <TargetIcon size={18} /> },
    ],
  },
  {
    label: '신뢰·관리',
    items: [
      { path: '/integrity', label: '무결성 검증', icon: <ShieldIcon size={18} /> },
      { path: '/contracts', label: '이행 조건 관리', icon: <ContractIcon size={18} /> },
      { path: '/design-predict', label: '설계 참고안', icon: <SparklesIcon size={18} /> },
      { path: '/users', label: '사용자 관리', icon: <UsersIcon size={18} />, adminOnly: true },
    ],
  },
]

const NAV = NAV_GROUPS.flatMap((group) => group.items)
const MUTATE_PATHS = new Set(['/inspection', '/contracts'])
const WORKFLOW_ITEMS = NAV_GROUPS[1].items

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <Link to="/" className="group flex min-w-0 items-center gap-3" aria-label="설화 BIM 작업 현황">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white shadow-soft">
        <BuildingIcon size={19} strokeWidth={2.2} />
      </div>
      {!compact && (
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-950">설화 BIM</div>
          <div className="truncate text-[10px] text-slate-500">시공 품질 워크스페이스</div>
        </div>
      )}
    </Link>
  )
}

function ProjectSwitcher({ compact = false }: { compact?: boolean }) {
  const { data: projects, isLoading } = useProjects()
  const { projectId, setProjectId } = useProjectSelection(projects)

  return (
    <label className={`flex min-w-0 items-center gap-2 ${compact ? 'w-full' : ''}`}>
      <span className="sr-only">활성 프로젝트</span>
      <FolderIcon size={15} className="shrink-0 text-slate-400" />
      <select
        value={projectId}
        onChange={(event) => setProjectId(event.target.value)}
        disabled={isLoading || !projects?.length}
        className="h-9 min-w-0 max-w-[260px] flex-1 rounded-md border border-slate-200 bg-white px-2.5 text-sm font-medium text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200 disabled:text-slate-400"
      >
        {!projects?.length && <option value="">프로젝트 없음</option>}
        {projects?.map((project) => (
          <option key={project.id} value={project.id}>{project.name}</option>
        ))}
      </select>
    </label>
  )
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { data: me } = useCurrentUser()
  const userCanMutate = canMutate(me?.role)
  const userIsAdmin = isAdmin(me?.role)

  return (
    <nav className="space-y-5" aria-label="주 메뉴">
      {NAV_GROUPS.map((group) => {
        const items = group.items.filter((item) => !item.adminOnly || userIsAdmin)
        if (!items.length) return null
        return (
          <div key={group.label}>
            <div className="mb-1.5 px-3 text-[10px] font-semibold uppercase text-slate-400">
              {group.label}
            </div>
            <div className="space-y-0.5">
              {items.map((item) => {
                const restricted = !userCanMutate && MUTATE_PATHS.has(item.path)
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={onNavigate}
                    title={restricted ? '조회 전용 권한' : undefined}
                    className={({ isActive }) =>
                      `group flex h-10 items-center gap-3 rounded-md px-3 text-sm transition-colors ${
                        isActive
                          ? 'bg-slate-900 font-medium text-white'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-700'}>
                          {item.icon}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {item.code && (
                          <span className={`font-mono text-[10px] ${isActive ? 'text-white/60' : 'text-slate-400'}`}>
                            {item.code}
                          </span>
                        )}
                        {restricted && <ShieldIcon size={12} className={isActive ? 'text-white/60' : 'text-slate-400'} />}
                      </>
                    )}
                  </NavLink>
                )
              })}
            </div>
          </div>
        )
      })}
    </nav>
  )
}

function UserPill({ stacked = false }: { stacked?: boolean }) {
  const { data: me } = useCurrentUser()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  if (!me) return null

  const roleStyles: Record<string, string> = {
    admin: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
    inspector: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    viewer: 'bg-slate-100 text-slate-600 ring-slate-200',
  }

  return (
    <div className={`flex items-center gap-2 ${stacked ? 'w-full justify-between' : ''}`}>
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
          {(me.full_name ?? me.email).slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="max-w-[150px] truncate font-medium text-slate-900">
              {me.full_name ?? me.email.split('@')[0]}
            </span>
            <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ring-1 ring-inset ${roleStyles[me.role] ?? roleStyles.viewer}`}>
              {ROLE_LABEL[me.role]}
            </span>
          </div>
          <div className="max-w-[190px] truncate text-[10px] text-slate-500">{me.email}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={async () => {
          await logout(queryClient)
          navigate('/login')
        }}
        className="icon-button"
        title="로그아웃"
        aria-label="로그아웃"
      >
        <LogoutIcon size={16} />
      </button>
    </div>
  )
}

function Sidebar() {
  return (
    <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
      <div className="border-b border-slate-100 px-5 py-5"><Brand /></div>
      <div className="flex-1 overflow-y-auto px-3 py-5"><SidebarNav /></div>
      <div className="space-y-3 border-t border-slate-100 p-4">
        <a
          href="https://gitea.gru.farm/airkjw/seolhwa-bim/src/branch/main/docs/manual"
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 rounded-md px-2 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-900"
        >
          <LinkIcon size={14} /> 운영 매뉴얼
        </a>
        <UserPill stacked />
      </div>
    </aside>
  )
}

function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <button type="button" aria-label="메뉴 닫기" onClick={onClose} className="absolute inset-0 bg-slate-950/40" />
      <aside className="absolute inset-y-0 left-0 flex w-[min(86vw,320px)] flex-col bg-white shadow-2xl">
        <div className="flex h-14 items-center justify-between border-b border-slate-100 px-4">
          <Brand />
          <button type="button" className="icon-button" onClick={onClose} aria-label="메뉴 닫기">
            <XIcon size={18} />
          </button>
        </div>
        <div className="border-b border-slate-100 p-4"><ProjectSwitcher compact /></div>
        <div className="flex-1 overflow-y-auto px-3 py-4"><SidebarNav onNavigate={onClose} /></div>
        <div className="border-t border-slate-100 p-4"><UserPill stacked /></div>
      </aside>
    </div>
  )
}

function WorkflowNav() {
  const location = useLocation()
  if (!WORKFLOW_ITEMS.some((item) => location.pathname.startsWith(item.path))) return null

  return (
    <div className="sticky top-14 z-20 border-b border-slate-200 bg-white/95 lg:top-16">
      <nav className="scrollbar-none mx-auto flex max-w-screen-2xl overflow-x-auto px-4 sm:px-6 lg:px-10" aria-label="검측 업무 단계">
        {WORKFLOW_ITEMS.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-xs font-medium transition-colors ${
                isActive
                  ? 'border-slate-900 text-slate-950'
                  : 'border-transparent text-slate-500 hover:text-slate-900'
              }`
            }
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full border border-current font-mono text-[10px]">
              {item.code}
            </span>
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}

function currentPageLabel(pathname: string) {
  if (pathname.startsWith('/projects/')) return '프로젝트 상세'
  return NAV.find((item) => item.path === pathname)?.label ?? '설화 BIM'
}

function Chrome({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white/95 px-4 backdrop-blur lg:h-16 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <button type="button" className="icon-button lg:hidden" onClick={() => setDrawerOpen(true)} aria-label="메뉴 열기">
              <MenuIcon size={19} />
            </button>
            <div className="lg:hidden"><Brand compact /></div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-900">{currentPageLabel(location.pathname)}</div>
              <div className="hidden text-[10px] text-slate-400 lg:block">설화 BIM 업무 워크스페이스</div>
            </div>
          </div>
          <div className="hidden min-w-0 items-center gap-5 lg:flex">
            <ProjectSwitcher />
            <UserPill />
          </div>
        </header>
        <WorkflowNav />
        <main className="flex-1 px-4 py-5 sm:px-6 lg:px-10 lg:py-8">
          <div className="mx-auto w-full max-w-screen-2xl animate-fade-in">{children}</div>
        </main>
        <footer className="border-t border-slate-200 bg-white px-4 py-3 text-center text-[10px] text-slate-400">
          주식회사 설화 · BIM 기반 시공 검측 자동화 시스템
        </footer>
      </div>
    </div>
  )
}

function PageFallback() {
  return (
    <div className="flex min-h-[45vh] items-center justify-center text-slate-400">
      <Spinner size={22} />
      <span className="ml-2 text-sm">화면 불러오는 중</span>
    </div>
  )
}

function AppPage({ children }: { children: ReactNode }) {
  return <Chrome><Suspense fallback={<PageFallback />}>{children}</Suspense></Chrome>
}

function isPublicPath(pathname: string): boolean {
  return pathname === '/login' || pathname.startsWith('/verify/')
}

function AuthGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { data: me, isLoading, isError } = useCurrentUser()
  if (isPublicPath(location.pathname)) {
    if (location.pathname === '/login' && me) return <Navigate to="/" replace />
    return <>{children}</>
  }
  if (isLoading) return <PageFallback />
  if (isError || !me) return <Navigate to="/login" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <AuthGate>
      <Routes>
        <Route path="/login" element={<Suspense fallback={<PageFallback />}><Login /></Suspense>} />
        <Route path="/verify/:reportId" element={<Suspense fallback={<PageFallback />}><PublicVerify /></Suspense>} />
        <Route path="/" element={<AppPage><Home /></AppPage>} />
        <Route path="/projects" element={<AppPage><Projects /></AppPage>} />
        <Route path="/projects/:id" element={<AppPage><ProjectDetail /></AppPage>} />
        <Route path="/bim-compare" element={<AppPage><BimCompare /></AppPage>} />
        <Route path="/site-photo" element={<AppPage><SitePhoto /></AppPage>} />
        <Route path="/inspection" element={<AppPage><Inspection /></AppPage>} />
        <Route path="/defects" element={<AppPage><Defects /></AppPage>} />
        <Route path="/integrity" element={<AppPage><Integrity /></AppPage>} />
        <Route path="/design-predict" element={<AppPage><DesignPredict /></AppPage>} />
        <Route path="/contracts" element={<AppPage><Contracts /></AppPage>} />
        <Route path="/users" element={<AppPage><Users /></AppPage>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthGate>
  )
}
